import { useState } from 'react';
import { AlertTriangle, ArrowLeft, Check, Gift, Loader2, Ticket, Trash2 } from 'lucide-react';
import {
  SEUIL_REVENUS_CENTIMES, ajouterCode, euros, libelleTarif, retirerCode, type Devis,
} from '../../../lib/orders/devis';

export interface SemaineRecapitulee {
  sessionId: string;
  stage: string;
  centre: string;
  /** Déjà mises en forme : « 19/10/2026 - 23/10/2026 ». */
  dates: string;
}

interface RecapitulatifInscriptionProps {
  kidId: string;
  semaines: readonly SemaineRecapitulee[];
  devis: Devis | null;
  devisEnCours: boolean;
  erreurDevis: string | null;
  reduit: boolean;
  onBasculerReduit: () => void;
  codes: string[];
  onCodes: (codes: string[]) => void;
  enCours: boolean;
  onConfirmer: () => void;
  onRetour: () => void;
  libelleRetour?: string;
}

/**
 * Le récapitulatif d'une inscription par l'administration : les semaines au
 * tarif du devis, la déclaration du tarif réduit, les codes cadeaux, et le
 * bouton qui commande.
 *
 * Partagé par « Inscription admin » et par la liste d'attente, qui inscrit
 * l'enfant par le même chemin. Aucun prix ne se calcule ici : tout vient du
 * devis (`quote_order`), et la commande (`create_order`) le refait en base.
 */
export default function RecapitulatifInscription({
  kidId, semaines, devis, devisEnCours, erreurDevis, reduit, onBasculerReduit,
  codes, onCodes, enCours, onConfirmer, onRetour, libelleRetour = 'Retour',
}: RecapitulatifInscriptionProps) {
  const [saisieCode, setSaisieCode] = useState('');

  const soumettreCode = (e: React.FormEvent) => {
    e.preventDefault();
    onCodes(ajouterCode(codes, saisieCode));
    setSaisieCode('');
  };

  return (
    <>
      <div className="border rounded-lg overflow-x-auto mb-6">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Centre</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dates</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tarif</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Prix</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {semaines.map((s) => {
              /*
                Le tarif affiche est celui du DEVIS, pas un calcul de cet
                ecran. En attendant sa reponse, on montre un tiret plutot
                qu'un montant qui pourrait etre faux.
              */
              const ligne = devis?.lines.find(
                (l) => l.session_id === s.sessionId && l.kid_id === kidId
              );
              const offerte = (ligne?.gratuity_cents ?? 0) > 0;
              return (
                <tr key={s.sessionId}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{s.stage}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{s.centre}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{s.dates}</td>
                  <td className="px-4 py-3 text-sm">
                    {ligne ? (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        ligne.price_type.includes('reduced')
                          ? 'bg-green-100 text-green-700'
                          : ligne.price_type.includes('local')
                            ? 'bg-teal-100 text-teal-700'
                            : 'bg-gray-100 text-gray-700'
                      }`}>
                        {libelleTarif(ligne.price_type)}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                    {offerte && (
                      <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                        Offerte — {ligne?.gratuity_code}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                    {ligne ? (
                      offerte ? (
                        <>
                          <span className="text-gray-400 line-through mr-1">{euros(ligne.amount_cents)}</span>
                          <span className="text-green-700">0,00 €</span>
                        </>
                      ) : euros(ligne.amount_cents)
                    ) : (devisEnCours ? '…' : '—')}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-gray-50">
            {devis && devis.gratuity_cents > 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-2 text-sm text-green-700 text-right">Codes cadeaux</td>
                <td className="px-4 py-2 text-sm text-green-700 text-right">−{euros(devis.gratuity_cents)}</td>
              </tr>
            )}
            <tr>
              <td colSpan={4} className="px-4 py-3 text-sm font-bold text-gray-900 text-right">Montant facturé</td>
              <td className="px-4 py-3 text-base font-bold text-gray-900 text-right">
                {devis ? euros(devis.amount_cents) : (devisEnCours ? '…' : '—')}
              </td>
            </tr>
            {devis && devis.provision_applicable_cents > 0 && (
              <>
                <tr>
                  <td colSpan={4} className="px-4 py-2 text-sm text-blue-700 text-right">
                    Payé par la provision de la famille
                  </td>
                  <td className="px-4 py-2 text-sm text-blue-700 text-right">
                    −{euros(devis.provision_applicable_cents)}
                  </td>
                </tr>
                <tr>
                  <td colSpan={4} className="px-4 py-3 text-sm font-bold text-gray-900 text-right">Reste à payer</td>
                  <td className="px-4 py-3 text-base font-bold text-gray-900 text-right">
                    {euros(devis.remaining_cents)}
                  </td>
                </tr>
              </>
            )}
          </tfoot>
        </table>
      </div>

      {erreurDevis && (
        <div className="mb-6 p-4 rounded-lg border border-red-200 bg-red-50 flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{erreurDevis}</p>
        </div>
      )}

      {/*
        La case est TOUJOURS proposee. L'ancienne version la masquait
        quand `calculatedReducedPrice` etait nul — donc precisement
        lorsque le calcul cote ecran avait echoue. C'est la base qui sait
        si un tarif reduit existe, et elle rend le tarif normal sinon.
      */}
      <div className="mb-6 p-4 rounded-lg border border-gray-200 bg-gray-50">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={reduit}
            onChange={onBasculerReduit}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
          />
          <div>
            <p className="text-sm font-medium text-gray-900">Appliquer le tarif réduit</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Le parent déclare sur l'honneur un revenu net du ménage inférieur
              à {euros(SEUIL_REVENUS_CENTIMES)} par mois. La déclaration est conservée
              avec cette commande.
            </p>
          </div>
        </label>
      </div>

      {/* Les codes cadeaux, cumulables */}
      <div className="mb-6 p-4 rounded-lg border border-gray-200">
        <div className="flex items-center mb-3">
          <Gift className="h-4 w-4 text-teal-600 mr-2" />
          <p className="text-sm font-medium text-gray-900">Codes cadeaux</p>
        </div>
        <form onSubmit={soumettreCode} className="flex gap-2">
          <input
            type="text"
            value={saisieCode}
            onChange={(e) => setSaisieCode(e.target.value)}
            placeholder="Ex. AUT-2026"
            className="flex-grow rounded-lg border-gray-300 focus:border-teal-500 focus:ring-teal-500 text-sm"
          />
          <button type="submit" className="btn-outline whitespace-nowrap" disabled={!saisieCode.trim()}>
            Ajouter
          </button>
        </form>
        {codes.length > 0 && (
          <ul className="mt-3 flex flex-wrap gap-2">
            {codes.map((code) => (
              <li key={code} className="inline-flex items-center bg-gray-50 rounded-lg px-2.5 py-1 text-sm">
                <Ticket className="h-3.5 w-3.5 mr-1.5 text-teal-600" />
                {code}
                <button
                  onClick={() => onCodes(retirerCode(codes, code))}
                  className="ml-2 text-gray-400 hover:text-red-600"
                  aria-label={`Retirer le code ${code}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-2 text-xs text-gray-500">
          Cumulables. Chaque code rend gratuite la semaine la moins chère parmi celles
          qu'il couvre.
        </p>
      </div>

      {/* Confirm button */}
      <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t">
        <button onClick={onRetour} disabled={enCours} className="btn-outline flex items-center gap-2">
          <ArrowLeft className="h-4 w-4" />
          {libelleRetour}
        </button>
        <button
          onClick={onConfirmer}
          disabled={enCours || devisEnCours || !devis}
          className="btn-primary flex items-center gap-2 px-6 disabled:opacity-50"
        >
          {enCours ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Inscription en cours...
            </>
          ) : (
            <>
              <Check className="h-4 w-4" />
              Confirmer l'inscription
            </>
          )}
        </button>
      </div>
    </>
  );
}
